import { useState } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Upload, X, Loader2, FileText, Music, Film } from 'lucide-react';
import { toast } from 'sonner';

interface UploadableFileInputProps {
  currentFileUrl?: string | null;
  onFileUpload: (url: string) => void;
  onFileRemove?: () => void;
  label?: string;
  acceptedTypes?: 'images' | 'videos' | 'all';
}

export function UploadableFileInput({
  currentFileUrl,
  onFileUpload,
  onFileRemove,
  label = 'File',
  acceptedTypes = 'images',
}: UploadableFileInputProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [filePreview, setFilePreview] = useState<{
    type: string;
    url: string;
  } | null>(currentFileUrl ? { type: 'existing', url: currentFileUrl } : null);

  const getAcceptString = () => {
    switch (acceptedTypes) {
      case 'images':
        return 'image/png, image/jpeg, image/jpg, image/webp, image/gif';
      case 'videos':
        return 'video/mp4, video/webm, video/quicktime';
      case 'all':
        return 'image/png, image/jpeg, image/jpg, image/webp, image/gif, video/mp4, video/webm, application/pdf, .pdf';
      default:
        return 'image/png, image/jpeg';
    }
  };

  const getFileTypeLabel = (url: string) => {
    if (url.includes('.pdf')) return 'PDF Document';
    if (url.includes('video')) return 'Video File';
    if (url.includes('image')) return 'Image';
    const ext = url.split('.').pop()?.toUpperCase();
    return ext ? `${ext} File` : 'File';
  };

  const getFileIcon = (url: string) => {
    if (url.includes('.pdf')) return <FileText className="w-8 h-8 text-red-500" />;
    if (url.includes('video')) return <Film className="w-8 h-8 text-blue-500" />;
    if (url.includes('audio')) return <Music className="w-8 h-8 text-purple-500" />;
    return <FileText className="w-8 h-8 text-gray-500" />;
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const maxSize = 100 * 1024 * 1024; // 100MB
    if (file.size > maxSize) {
      toast.error('File size must be less than 100MB');
      e.target.value = '';
      return;
    }

    // Show local preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setFilePreview({
        type: file.type.startsWith('image/') ? 'image' : 'file',
        url: reader.result as string,
      });
    };
    reader.readAsDataURL(file);

    // Upload to Cloudinary
    setIsUploading(true);
    try {
      const cloudName = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME as string | undefined;
      const uploadPreset = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET as string | undefined;

      if (!cloudName || !uploadPreset) {
        throw new Error(
          'Cloudinary credentials not configured. Please add VITE_CLOUDINARY_CLOUD_NAME and VITE_CLOUDINARY_UPLOAD_PRESET to .env.local'
        );
      }

      // Prepare form data for Cloudinary
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', uploadPreset);

      // Upload to Cloudinary
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${cloudName}/auto/upload`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Upload failed');
      }

      const data = await response.json();
      const secureUrl = data.secure_url;

      onFileUpload(secureUrl);
      toast.success('File uploaded successfully');
    } catch (error: any) {
      console.error('Upload error:', error);

      let errorMsg = 'Failed to upload file. Please try again.';
      if (error?.message?.includes('credentials')) {
        errorMsg = error.message;
      } else if (error?.message?.includes('not authenticated')) {
        errorMsg = 'Please sign in to upload files';
      } else if (error?.message?.includes('Upload failed')) {
        errorMsg = error.message;
      }

      toast.error(errorMsg);
      setFilePreview(currentFileUrl ? { type: 'existing', url: currentFileUrl } : null);
      e.target.value = '';
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveFile = () => {
    setFilePreview(null);
    if (onFileRemove) {
      onFileRemove();
    }
    const fileInput = document.getElementById(`file-input-${label}`) as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  return (
    <div className="space-y-2">
      <Label>{label} (optional)</Label>

      {!filePreview ? (
        <label
          htmlFor={`file-input-${label}`}
          className="block border-2 border-dashed border-border rounded-xl p-6 text-center hover:border-primary/50 transition-colors cursor-pointer"
        >
          <Upload className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Click to upload or drag and drop
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {acceptedTypes === 'images' && 'PNG, JPG, WEBP up to 100MB'}
            {acceptedTypes === 'videos' && 'MP4, WebM up to 100MB'}
            {acceptedTypes === 'all' && 'Images, videos, PDFs up to 100MB'}
          </p>
        </label>
      ) : filePreview.type === 'image' ? (
        <div className="relative rounded-xl overflow-hidden border-2 border-border">
          <img
            src={filePreview.url}
            alt="Preview"
            className="w-full h-40 object-cover"
          />
          <Button
            variant="destructive"
            size="sm"
            className="absolute top-2 right-2"
            onClick={handleRemoveFile}
          >
            <X className="w-4 h-4 mr-1" />
            Remove
          </Button>
        </div>
      ) : (
        <div className="relative rounded-xl overflow-hidden border-2 border-border p-6 bg-muted/50">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              {getFileIcon(filePreview.url)}
              <div>
                <p className="font-medium text-sm">{getFileTypeLabel(filePreview.url)}</p>
                <p className="text-xs text-muted-foreground">
                  {filePreview.url.split('/').pop()?.substring(0, 40)}...
                </p>
              </div>
            </div>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleRemoveFile}
            >
              <X className="w-4 h-4 mr-1" />
              Remove
            </Button>
          </div>
        </div>
      )}

      <input
        type="file"
        accept={getAcceptString()}
        className="hidden"
        id={`file-input-${label}`}
        onChange={handleFileSelect}
        disabled={isUploading}
      />

      {isUploading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          Uploading file...
        </div>
      )}
    </div>
  );
}
